import cloneDeep from "lodash.clonedeep";
import lineDistance from "@turf/line-distance";
import along from "@turf/along";

import { styleConfigs } from "../configs";
import {
  parseObjectFields,
  getRequestConfig,
  jsonToGeojson,
  debounce,
  getFieldsAggregation,
  getDatasourceById
} from "@/utils";

export class TrackController {
  constructor(map) {
    this.map = map;
    this.handlers = {};
    this.popupEnterHandlers = {};
    this.popupOutHandlers = {};
    this.clickHandlers = {};
    this.animations = {};
    this.routes = {};
    this.distances = {};
  }

  removeLayersById(id) {
    const mapgl = this.map.mapgl;

    mapgl.off("moveend", this.handlers[id]);

    if (mapgl.getLayer(id)) mapgl.removeLayer(id);
    if (mapgl.getSource(id)) mapgl.removeSource(id);
  }

  async toggleLayer(layer) {
    const mapgl = this.map.mapgl;
    const { source_id } = layer;
    const { loadingLayers, activeLayers } = this.map.$store.state.map;
    if (loadingLayers.indexOf(source_id) > -1) return;
    const index = activeLayers.indexOf(source_id);
    this.map.$store.commit("TOGGLE_LOADING_LAYER", source_id);

    if (index > -1) {
      this.map.$store.commit("TOGGLE_ACTIVE_LAYER", source_id);
      this.removeLayersById(source_id);
      this.clearAnimation(source_id);
      // if (this.map.layerSettingsId === source_id) {
      //   this.map.isSecondPanelActive = false;
      //   this.map.layerSettingsId = "";
      //   this.map.$nextTick(() => this.map.mapgl.resize());
      // }
    } else {
      const data = await getDatasourceById(this.map, source_id);
      const { id, geom_type } = data;

      // get fields and aggregation
      const fieldsUrl = `objectFields/${id}`;
      const fieldsResponse = await this.map.$store.dispatch("REQUEST", {
        url: fieldsUrl
      });
      this.map.objectFields[id] = parseObjectFields(fieldsResponse, false).map(
        item => ({
          ...item,
          children: [],
          fn_type: item.type === "belongs_to" ? "concat" : "count",
          parent_id: id,
          source_name: item.source_name,
          isLoading: false
        })
      );
      this.map.$store.commit("SET_OBJECT_FIELDS", {
        source_id: id,
        fields: this.map.objectFields[id]
      });

      const only = this.map.requestedFields[id] || [];
      await getFieldsAggregation(this.map, only, id);

      // set requested fields
      if (!this.map.requestedFields[id]) {
        this.map.requestedFields[id] = ["id", "geom"];
      }

      // load layer handler
      const loadLayerData = async () => {
        const zoom = mapgl.getZoom();
        const config = getRequestConfig(this.map, id);

        config.only = [...config.only, "icao", "times"];
        config.limit = 3000;

        try {
          // this.map.mapLoading = true;
          const url = `trackInfo/${id}?config=${JSON.stringify(
            config
          )}&zoom=${zoom}`;
          const { data } = await this.map.$store.dispatch("REQUEST", {
            url
          });
          const layerData = jsonToGeojson(
            Object.keys(data).map(id => data[id])
          );
          const mapSource = mapgl.getSource(id);
          const mapLayer = mapgl.getLayer(id);

          layerData.features.forEach(f => {
            f.properties.activity = String(f.properties.activity);
          });

          if (mapSource) {
            this.clearAnimation(id);
            mapSource.setData(layerData);
            this.createAnimation(id, layerData);
          } else {
            mapgl.addSource(id, {
              type: "geojson",
              data: layerData
            });
            this.createAnimation(id, layerData);
          }

          if (!mapLayer) {
            const options = {
              id,
              type: "circle",
              source: id,
              paint: {
                "circle-radius": 4,
                "circle-color": [
                  "match",
                  ["get", "activity"],
                  "true",
                  "#55b77e",
                  "rgb(240, 240, 240)"
                ],
                "circle-stroke-width": 1,
                "circle-stroke-color": "rgb(200, 200, 200)"
              }
            };

            mapgl.addLayer(options);
          }

          // this.map.mapLoading = false;
          this.map.isUpdate = !this.map.isUpdate;
        } catch (error) {
          console.log(error);
        }
      };

      this.handlers[id] = debounce(loadLayerData, 200);
      mapgl.on("moveend", this.handlers[id]);

      // set style config
      if (!this.map.styleConfig[id])
        this.map.$set(
          this.map.styleConfig,
          id,
          cloneDeep(styleConfigs[geom_type])
        );

      await loadLayerData();
      this.map.$store.commit("TOGGLE_ACTIVE_LAYER", id);
    }

    this.map.$store.commit("TOGGLE_LOADING_LAYER", source_id);
  }

  clearAnimation(id) {
    if (this.animations[id]) cancelAnimationFrame(this.animations[id]);
  }

  createAnimation(id, data) {
    const mapgl = this.map.mapgl;
    let { features } = data;

    this.distances[id] = {};
    this.routes[id] = {};

    const { animations, routes, handlers, distances } = this;

    features.forEach(({ properties }) => {
      // const objectId = properties.id;
      const objectId = properties.vehicle_id;
      const route = properties.predict_line;
      const distance = lineDistance(route, "kilometres");

      this.routes[id][objectId] = route;
      this.distances[id][objectId] = distance;
    });

    const options = {
      duration: 5000,
      timing: timeFraction => timeFraction,
      draw: function(progress) {
        if (progress < 0) return;

        const source = mapgl.getSource(id);
        features = features.map(feature => {
          const { geometry, properties } = feature;
          const objectId = properties.vehicle_id;
          const distance = distances[id][objectId];

          if (distance) {
            const newCoords = along(
              routes[id][objectId],
              distance * progress,
              "kilometres"
            );

            geometry.coordinates = newCoords.geometry.coordinates;
          }

          return feature;
        });

        if (source) {
          source.setData({
            type: "FeatureCollection",
            features: features
          });
        }
      }
    };

    function animate(options) {
      const start = performance.now();

      requestAnimationFrame(function animate(time) {
        let timeFraction = (time - start) / options.duration;
        if (timeFraction > 1) timeFraction = 1;

        const progress = options.timing(timeFraction);

        options.draw(progress);

        if (timeFraction < 1) {
          animations[id] = requestAnimationFrame(animate);
        } else {
          handlers[id]();
        }
      });
    }

    animate(options);
  }
}
